/**
 * @module DashboardPage
 *
 * ダッシュボード画面コンポーネント
 * - サマリーカード（総資産数、期限通知件数、ライセンス超過件数）
 * - 資産区分別円グラフ（recharts）
 * - 状態別棒グラフ
 * - 期限通知一覧テーブル（超過: 赤色、接近中: 黄色）
 * - ライセンスサマリーテーブル
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4, 9.1, 9.2, 9.3, 9.4, 10.4
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import type { DashboardSummary } from '@shared/types/dashboard.types';
import type { AssetCategory, AssetStatus } from '@shared/types/asset.types';

// ============================================================
// 型定義
// ============================================================

interface NotificationAsset {
  id: string;
  assetNumber: string;
  assetName: string | null;
  category: string;
  customerName: string | null;
  deadlineType: 'renewal' | 'maintenance';
  deadlineDate: string;
  daysRemaining: number;
  status: 'overdue' | 'approaching';
  managerId: string | null;
  managerName?: string;
  notificationDisabled: boolean;
  sourceType?: 'asset' | 'contract' | 'license';
}

// ============================================================
// 定数: 日本語ラベル
// ============================================================

const CATEGORY_LABELS: Record<AssetCategory, string> = {
  pc: 'PC',
  server: 'サーバー',
  nas: 'NAS',
  network_device: 'ネットワーク機器',
  monitor: 'モニター',
  printer: 'プリンター',
  software_license: 'ソフトウェアライセンス',
  smartphone: 'スマートフォン',
  tablet: 'タブレット',
  other: 'その他',
};

const STATUS_LABELS: Record<AssetStatus, string> = {
  in_use: '使用中',
  in_storage: '保管中',
  under_repair: '修理中',
  disposed: '廃棄済み',
  lost: '紛失',
};

const DEADLINE_TYPE_LABELS: Record<string, string> = {
  renewal: '更新期限',
  maintenance: '保守期限',
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  asset: '資産',
  contract: '契約',
  license: 'ライセンス',
};

const PIE_COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#d32f2f',
  '#0097a7',
  '#fbc02d',
  '#5d4037',
  '#455a64',
  '#616161',
];

const STATUS_COLORS: Record<AssetStatus, string> = {
  in_use: '#1976d2',
  in_storage: '#388e3c',
  under_repair: '#f57c00',
  disposed: '#9e9e9e',
  lost: '#d32f2f',
};

// ============================================================
// サブコンポーネント
// ============================================================

interface SummaryCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold', color }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

interface CategoryPieChartProps {
  byCategory: Record<AssetCategory, number>;
}

const CategoryPieChart: React.FC<CategoryPieChartProps> = ({ byCategory }) => {
  const data = Object.entries(byCategory)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => ({
      name: CATEGORY_LABELS[key as AssetCategory] || key,
      value: count,
    }));

  if (data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        データがありません
      </Typography>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
          outerRadius={100}
          dataKey="value"
        >
          {data.map((_entry, index) => (
            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
          ))}
        </Pie>
        <RechartsTooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

interface StatusBarChartProps {
  byStatus: Record<AssetStatus, number>;
}

const StatusBarChart: React.FC<StatusBarChartProps> = ({ byStatus }) => {
  const data = Object.entries(byStatus).map(([key, count]) => ({
    name: STATUS_LABELS[key as AssetStatus] || key,
    count,
    fill: STATUS_COLORS[key as AssetStatus] || '#757575',
  }));

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis allowDecimals={false} />
        <RechartsTooltip />
        <Legend />
        <Bar dataKey="count" name="資産数">
          {data.map((entry, index) => (
            <Cell key={`bar-${index}`} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

interface NotificationTableProps {
  notifications: NotificationAsset[];
  onToggleNotification: (id: string) => void;
}

interface MergedNotification {
  id: string;
  assetNumber: string;
  assetName: string | null;
  category: string;
  customerName: string | null;
  deadlineTypes: string[];
  deadlineDate: string;
  daysRemaining: number;
  status: 'overdue' | 'approaching';
  notificationDisabled: boolean;
  sourceType?: 'asset' | 'contract' | 'license';
}

function mergeNotifications(notifications: NotificationAsset[]): MergedNotification[] {
  const map = new Map<string, MergedNotification>();
  for (const item of notifications) {
    const existing = map.get(item.id);
    if (existing) {
      if (!existing.deadlineTypes.includes(item.deadlineType)) {
        existing.deadlineTypes.push(item.deadlineType);
      }
      // Use the earlier deadline date
      const itemDate = new Date(item.deadlineDate).getTime();
      const existingDate = new Date(existing.deadlineDate).getTime();
      if (itemDate < existingDate) {
        existing.deadlineDate = typeof item.deadlineDate === 'string' ? item.deadlineDate : new Date(item.deadlineDate).toISOString();
        existing.daysRemaining = item.daysRemaining;
        existing.status = item.status;
      }
    } else {
      map.set(item.id, {
        id: item.id,
        assetNumber: item.assetNumber,
        assetName: item.assetName,
        category: item.category,
        customerName: item.customerName,
        deadlineTypes: [item.deadlineType],
        deadlineDate: typeof item.deadlineDate === 'string' ? item.deadlineDate : new Date(item.deadlineDate).toISOString(),
        daysRemaining: item.daysRemaining,
        status: item.status,
        notificationDisabled: item.notificationDisabled,
        sourceType: item.sourceType,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => new Date(a.deadlineDate).getTime() - new Date(b.deadlineDate).getTime());
}

const NotificationTable: React.FC<NotificationTableProps> = ({ notifications, onToggleNotification }) => {
  if (notifications.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        通知対象の資産はありません
      </Typography>
    );
  }

  const merged = mergeNotifications(notifications);

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>通知</TableCell>
            <TableCell>種別</TableCell>
            <TableCell>資産番号</TableCell>
            <TableCell>資産名称</TableCell>
            <TableCell>顧客</TableCell>
            <TableCell>区分</TableCell>
            <TableCell>期限種別</TableCell>
            <TableCell>期限日</TableCell>
            <TableCell>残日数</TableCell>
            <TableCell>状態</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {merged.map((item) => {
            const isOverdue = item.status === 'overdue';
            const isDisabled = item.notificationDisabled;
            const rowSx = isOverdue
              ? { backgroundColor: 'rgba(211, 47, 47, 0.08)', ...(isDisabled && { opacity: 0.7 }) }
              : { backgroundColor: 'rgba(237, 108, 2, 0.08)', ...(isDisabled && { opacity: 0.7 }) };

            return (
              <TableRow key={item.id} hover sx={rowSx}>
                <TableCell>
                  <Tooltip title={isDisabled ? '通知OFF' : '通知ON'}>
                    <IconButton
                      size="small"
                      onClick={() => onToggleNotification(item.id)}
                      color={isDisabled ? 'default' : 'primary'}
                    >
                      {isDisabled ? (
                        <NotificationsOffIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      ) : (
                        <NotificationsActiveIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    label={SOURCE_TYPE_LABELS[item.sourceType ?? 'asset'] || '資産'}
                    size="small"
                    variant="outlined"
                    color={item.sourceType === 'contract' ? 'info' : item.sourceType === 'license' ? 'secondary' : 'default'}
                  />
                </TableCell>
                <TableCell>{item.assetNumber}</TableCell>
                <TableCell>{item.assetName || '-'}</TableCell>
                <TableCell>{item.customerName || '-'}</TableCell>
                <TableCell>
                  {CATEGORY_LABELS[item.category as AssetCategory] || item.category}
                </TableCell>
                <TableCell>
                  {item.deadlineTypes.map((t) => DEADLINE_TYPE_LABELS[t] || t).join(' / ')}
                </TableCell>
                <TableCell>
                  {new Date(item.deadlineDate).toLocaleDateString('ja-JP')}
                </TableCell>
                <TableCell>
                  <Typography
                    component="span"
                    sx={{
                      color: isOverdue ? 'error.main' : 'warning.main',
                      fontWeight: 'bold',
                    }}
                  >
                    {isOverdue ? `${Math.abs(item.daysRemaining)}日超過` : `残${item.daysRemaining}日`}
                  </Typography>
                </TableCell>
                <TableCell>
                  {isOverdue ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                      <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                      超過
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                      <NotificationsIcon fontSize="small" sx={{ mr: 0.5 }} />
                      接近中
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface LicenseSummaryProps {
  licenses: DashboardSummary['licenses'];
}

const LicenseSummary: React.FC<LicenseSummaryProps> = ({ licenses }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>項目</TableCell>
          <TableCell align="right">件数</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>ライセンス総数</TableCell>
          <TableCell align="right">{licenses.total}</TableCell>
        </TableRow>
        <TableRow sx={{ backgroundColor: licenses.overused > 0 ? 'rgba(211, 47, 47, 0.08)' : undefined }}>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {licenses.overused > 0 && <WarningIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />}
              超過
            </Box>
          </TableCell>
          <TableCell align="right">
            <Typography
              component="span"
              sx={{ color: licenses.overused > 0 ? 'error.main' : undefined, fontWeight: licenses.overused > 0 ? 'bold' : undefined }}
            >
              {licenses.overused}
            </Typography>
          </TableCell>
        </TableRow>
        <TableRow sx={{ backgroundColor: licenses.lowRemaining > 0 ? 'rgba(237, 108, 2, 0.08)' : undefined }}>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {licenses.lowRemaining > 0 && <NotificationsIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />}
              残少
            </Box>
          </TableCell>
          <TableCell align="right">
            <Typography
              component="span"
              sx={{ color: licenses.lowRemaining > 0 ? 'warning.main' : undefined, fontWeight: licenses.lowRemaining > 0 ? 'bold' : undefined }}
            >
              {licenses.lowRemaining}
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

// ============================================================
// メインコンポーネント
// ============================================================

const DashboardPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const urlCustomerId = searchParams.get('customerId') || '';
  const urlCustomerName = searchParams.get('customerName') || '';
  const queryClient = useQueryClient();

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery<DashboardSummary>({
    queryKey: ['dashboard', 'summary', urlCustomerId],
    queryFn: async () => {
      const params = urlCustomerId ? `?customerId=${urlCustomerId}` : '';
      const res = await api.get(`/dashboard/summary${params}`);
      return res.data;
    },
  });

  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    error: notificationsError,
  } = useQuery<{ data: NotificationAsset[] }>({
    queryKey: ['dashboard', 'notifications', urlCustomerId],
    queryFn: async () => {
      const params = urlCustomerId ? `?customerId=${urlCustomerId}` : '';
      const res = await api.get(`/dashboard/notifications${params}`);
      return res.data;
    },
  });

  const toggleNotificationMutation = useMutation({
    mutationFn: async (assetId: string) => {
      const res = await api.put(`/assets/${assetId}/notification-toggle`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'notifications'] });
    },
  });

  const handleToggleNotification = (assetId: string) => {
    toggleNotificationMutation.mutate(assetId);
  };

  const isLoading = summaryLoading || notificationsLoading;
  const hasError = summaryError || notificationsError;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        ダッシュボードデータの取得に失敗しました。
      </Alert>
    );
  }

  if (!summary) {
    return null;
  }

  const notifications = notificationsData?.data ?? [];
  const notificationCount = summary.notifications.approachingCount + summary.notifications.overdueCount;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          ダッシュボード
        </Typography>
        {urlCustomerName && (
          <Chip label={urlCustomerName} color="primary" variant="outlined" size="small" />
        )}
      </Box>

      {/* グラフセクション */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              資産区分別
            </Typography>
            <CategoryPieChart byCategory={summary.byCategory} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              状態別集計
            </Typography>
            <StatusBarChart byStatus={summary.byStatus} />
          </Paper>
        </Grid>
      </Grid>

      {/* 期限通知一覧 */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          期限通知一覧
        </Typography>
        <NotificationTable notifications={notifications} onToggleNotification={handleToggleNotification} />
      </Paper>
    </Box>
  );
};

export default DashboardPage;
